import type { ElementType, HTMLAttributes, ReactNode, Ref } from "react";
import { cx } from "./cx";
import styles from "./Card.module.css";

export type CardPadding = "none" | "sm" | "md" | "lg";

// `title` est omis de HTMLAttributes avant d'être redéclaré : l'attribut HTML
// natif du même nom est un `string`, et l'intersection donnerait `string &
// ReactNode`, c'est-à-dire un type qu'aucun nœud React ne satisfait. Stat et
// EmptyState font déjà cet Omit ; Card l'avait oublié, et toute carte dont le
// titre n'est pas une chaîne nue échouait à la compilation.
export type CardProps = Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
  /**
   * Référence posée sur l'élément racine. Sans elle, un écran qui veut secouer
   * une carte au refus doit l'envelopper dans un div porteur de la ref — ce que
   * cinq écrans referaient chacun de son côté. React 19 transmet une ref passée
   * en prop, aucun forwardRef n'est nécessaire.
   */
  ref?: Ref<HTMLElement>;
  /** Titre de la carte. Rendu dans un `<h3>`, donc utilisable sous un `<h2>` de section. */
  title?: ReactNode;
  /** Précision sous le titre. */
  subtitle?: ReactNode;
  /** Boutons ou badges alignés à droite du titre. */
  actions?: ReactNode;
  /** Bandeau bas, séparé par un filet. */
  footer?: ReactNode;
  /** Défaut `md`. `none` quand la carte contient un tableau pleine largeur. */
  padding?: CardPadding;
  /** Relief au survol. À ne poser que si la carte entière est cliquable. */
  interactive?: boolean;
  /** Élément rendu. `article` ou `li` selon le contexte sémantique. */
  as?: "div" | "section" | "article" | "li";
};

/**
 * Surface de base de l'interface. Tout contenu de l'application vit dans une
 * carte ou dans une suite de cartes.
 *
 * Pour l'animer à l'arrivée, poser `data-anim="card"` dessus et
 * `useCardEntrance()` sur le conteneur parent.
 */
export function Card({
  title,
  subtitle,
  actions,
  footer,
  padding = "md",
  interactive = false,
  as = "div",
  className,
  children,
  ref,
  ...rest
}: CardProps) {
  // Les gestionnaires d'événements sont typés pour un `div` ; les rendre
  // polymorphes exigerait un `Card<T extends ElementType>` générique, dont le
  // coût de lecture dépasse largement le gain : `as` ne sert qu'à rendre la
  // bonne balise sémantique.
  const Balise = as as ElementType;
  const avecEntete = title !== undefined || subtitle !== undefined || actions !== undefined;
  return (
    <Balise
      ref={ref}
      className={cx(
        styles.carte,
        styles[padding],
        interactive && styles.interactive,
        className,
      )}
      {...rest}
    >
      {avecEntete && (
        <header className={styles.entete}>
          <div className={styles.titres}>
            {title !== undefined && <h3 className={styles.titre}>{title}</h3>}
            {subtitle !== undefined && (
              <p className={styles.soustitre}>{subtitle}</p>
            )}
          </div>
          {actions !== undefined && <div className={styles.actions}>{actions}</div>}
        </header>
      )}
      {children !== undefined && <div className={styles.corps}>{children}</div>}
      {footer !== undefined && <footer className={styles.pied}>{footer}</footer>}
    </Balise>
  );
}
